import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { track } from "../../utils/analytics";

const getPrefersReducedMotion = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const Confetti = () => {
  const prefersReducedMotion = getPrefersReducedMotion();
  const pieces = useMemo(() => {
    return Array.from({ length: 80 }).map((_, index) => ({
      id: index,
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      size: 6 + Math.random() * 8,
      duration: 2.2 + Math.random() * 1.6,
      rotate: Math.random() * 360,
      color: ["#DCFF02", "#22c55e", "#10b981", "#14b8a6", "#0ea5e9"][
        Math.floor(Math.random() * 5)
      ],
    }));
  }, []);

  if (prefersReducedMotion) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
      }}
      aria-hidden
    >
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translate3d(0, -120%, 0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate3d(0, 110vh, 0) rotate(360deg); opacity: 0; }
        }
      `}</style>
      {pieces.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.4,
            backgroundColor: p.color,
            opacity: 0.9,
            borderRadius: 2,
            transform: `rotate(${p.rotate}deg)`,
            animation: `confetti-fall ${p.duration}s linear ${p.delay}s forwards`,
          }}
        />
      ))}
    </div>
  );
};

const TYPES = {
  calendar: {
    icon: "/images/logos/googlecalendar.png",
    iconAlt: "Google Calendar",
    headline: "Calendar connected!",
    subtitle:
      "Your Google Calendar is linked. Your coach can now see your schedule and help you manage your time better.",
    mixpanelEvent: "Connected Calendar Page Viewed",
  },
  health: {
    icon: null,
    iconAlt: "Health Data",
    headline: "Health data connected!",
    subtitle:
      "Your health device is linked. Your coach can now see your sleep, activity, and workout data to give you personalized insights.",
    mixpanelEvent: "Connected Health Page Viewed",
  },
};

const containerStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  color: "#0f172a",
  padding: "24px",
  textAlign: "center",
};

const cardStyle = {
  width: "100%",
  maxWidth: 560,
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
  borderRadius: 24,
  padding: "40px 32px",
};

const headlineStyle = {
  fontSize: 36,
  lineHeight: 1.2,
  margin: "16px 0 12px",
  fontWeight: 800,
  color: "#0f172a",
};

const subtextStyle = {
  fontSize: 18,
  color: "#334155",
  margin: "0 0 28px",
};

const buttonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  background: "#DCFF02",
  color: "#111827",
  border: "1px solid rgba(220,255,2,0.7)",
  borderRadius: 999,
  padding: "16px 20px",
  fontSize: 16,
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 16px 32px rgba(220,255,2,0.2)",
};

const altLinkContainerStyle = { marginTop: 14 };
const altLinkStyle = {
  display: "inline-block",
  color: "#334155",
  textDecoration: "underline",
  fontSize: 14,
  fontWeight: 600,
  opacity: 0.9,
};

const iconBadgeStyle = {
  width: 88,
  height: 88,
  margin: "0 auto",
  borderRadius: "50%",
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const HealthIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19.5 12.572l-7.5 7.428l-7.5-7.428a5 5 0 1 1 7.5-6.566a5 5 0 1 1 7.5 6.572" />
  </svg>
);

export default function Connected() {
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") === "health" ? "health" : "calendar";
  const config = TYPES[type];

  useEffect(() => {
    track(config.mixpanelEvent);
  }, [config.mixpanelEvent]);

  const tryReturn = () => {
    track("Connected ReturnToMessages Clicked", {
      manual: true,
      type,
    });

    if (window.history && window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.href = "sms:";
  };

  return (
    <div style={containerStyle}>
      <Confetti />
      <div style={cardStyle}>
        <div style={iconBadgeStyle}>
          {config.icon ? (
            <img
              src={config.icon}
              alt={config.iconAlt}
              style={{ width: 40, height: 40 }}
            />
          ) : (
            <HealthIcon />
          )}
        </div>
        <h1 style={headlineStyle}>{config.headline}</h1>
        <p style={subtextStyle}>{config.subtitle}</p>

        <button style={buttonStyle} onClick={tryReturn}>
          Return to messages
          <img
            src="/images/right-up.png"
            alt=""
            style={{ width: 18, height: 18, marginLeft: 10 }}
          />
        </button>

        <div style={altLinkContainerStyle}>
          <a href="whatsapp://app" style={altLinkStyle}>
            Open WhatsApp instead
          </a>
        </div>
      </div>
    </div>
  );
}
